import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  MapPin, 
  Plus, 
  Eye, 
  Edit, 
  Trash2,
  Search,
  Filter,
  Package
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { productService } from '../../services/productService';
import { Location } from '../../types';

function LocationsList() {
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    magasin: '',
    activeOnly: true
  });

  useEffect(() => {
    loadLocations();
  }, [filters]);

  const loadLocations = async () => {
    try {
      setLoading(true);
      const data = await productService.getLocations();
      
      let filtered = data;
      
      if (filters.search) {
        const search = filters.search.toLowerCase();
        filtered = filtered.filter(l => 
          l.fullCode.toLowerCase().includes(search) ||
          l.commentaire?.toLowerCase().includes(search) ||
          l.magasin.toLowerCase().includes(search)
        );
      }
      
      if (filters.magasin) {
        filtered = filtered.filter(l => l.magasin === filters.magasin);
      }
      
      if (filters.activeOnly) {
        filtered = filtered.filter(l => l.isActive);
      }
      
      setLocations(filtered);
    } catch (error) {
      addNotification({
        userId: user?.id || '',
        title: 'Erreur de chargement',
        message: 'Impossible de charger les emplacements',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (locationId: string, isActive: boolean) => {
    if (!user) return;

    try {
      await productService.updateLocation(locationId, { isActive: !isActive });
      addNotification({
        userId: user.id,
        title: isActive ? 'Emplacement désactivé' : 'Emplacement activé',
        message: `L'emplacement a été ${isActive ? 'désactivé' : 'activé'} avec succès`,
        type: 'success'
      });
      loadLocations();
    } catch (error) {
      addNotification({
        userId: user?.id || '',
        title: 'Erreur',
        message: 'Impossible de modifier le statut',
        type: 'error'
      });
    }
  };

  const handleDeleteLocation = async (locationId: string) => {
    if (!user || !confirm('Êtes-vous sûr de vouloir supprimer cet emplacement ?')) return;

    try {
      await productService.deleteLocation(locationId);
      addNotification({
        userId: user.id,
        title: 'Emplacement supprimé',
        message: 'L\'emplacement a été supprimé avec succès',
        type: 'success'
      });
      loadLocations();
    } catch (error) {
      addNotification({
        userId: user?.id || '',
        title: 'Erreur',
        message: 'Impossible de supprimer l\'emplacement',
        type: 'error'
      });
    }
  };

  const canManageLocations = user?.role === 'admin' || user?.role === 'responsable_depot' || user?.role === 'responsable_bureau';

  const uniqueMagasins = [...new Set(locations.map(l => l.magasin))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="glass-card rounded-xl card-hover">
        <div className="px-6 py-6 sm:p-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg shadow-lg">
                <MapPin className="h-6 w-6 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900">Emplacements</h1>
            </div>
            
            {canManageLocations && (
              <Link
                to="/products/locations/new"
                className="btn-primary px-4 py-2 text-white text-sm rounded-lg font-medium transition-all duration-200 flex items-center space-x-2"
              >
                <Plus className="h-4 w-4" />
                <span>Nouvel emplacement</span>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="glass-card rounded-xl">
        <div className="px-6 py-5 sm:px-8">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher (code, commentaire)..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white/80"
              />
            </div>

            {/* Magasin Filter */}
            <select
              value={filters.magasin}
              onChange={(e) => setFilters({ ...filters, magasin: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white/80"
            >
              <option value="">Tous les magasins</option>
              {uniqueMagasins.map(magasin => (
                <option key={magasin} value={magasin}>
                  {magasin}
                </option>
              ))}
            </select>

            {/* Status Filter */}
            <label className="flex items-center space-x-2 text-sm">
              <input
                type="checkbox"
                checked={filters.activeOnly}
                onChange={(e) => setFilters({ ...filters, activeOnly: e.target.checked })}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-gray-700">Actifs uniquement</span>
            </label>

            <button
              onClick={() => setFilters({ search: '', magasin: '', activeOnly: true })}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white/80 hover:bg-white transition-colors"
            >
              Réinitialiser
            </button>
          </div>
        </div>
      </div>

      {/* Locations List */}
      <div className="glass-card rounded-xl">
        <div className="px-6 py-5 sm:px-8 border-b border-gray-200/50">
          <h3 className="text-lg font-medium text-gray-900">
            Emplacements ({locations.length})
          </h3>
        </div>

        {locations.length === 0 ? (
          <div className="text-center py-12">
            <MapPin className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun emplacement</h3>
            <p className="mt-1 text-sm text-gray-500">
              Créez votre premier emplacement
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Magasin
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Position
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Commentaire
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white/50 divide-y divide-gray-200">
                {locations.map((location) => (
                  <tr key={location.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <MapPin className="h-4 w-4 text-blue-500 mr-2" />
                        <span className="text-sm font-mono font-medium text-gray-900">
                          {location.fullCode}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {location.magasin}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      Allée {location.allee} - Travée {location.travee} - Bac {location.bac}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {location.commentaire || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        location.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {location.isActive ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        {canManageLocations && (
                          <>
                            <Link
                              to={`/products/locations/${location.id}/edit`}
                              className="text-blue-600 hover:text-blue-900 p-1 rounded"
                              title="Modifier"
                            >
                              <Edit className="h-4 w-4" />
                            </Link>
                            
                            <button
                              onClick={() => handleToggleStatus(location.id, location.isActive)}
                              className={`p-1 rounded ${
                                location.isActive 
                                  ? 'text-red-600 hover:text-red-900' 
                                  : 'text-green-600 hover:text-green-900'
                              }`}
                              title={location.isActive ? 'Désactiver' : 'Activer'}
                            >
                              <Package className="h-4 w-4" />
                            </button>
                            
                            <button
                              onClick={() => handleDeleteLocation(location.id)}
                              className="text-red-600 hover:text-red-900 p-1 rounded"
                              title="Supprimer"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default LocationsList;